import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { storePasswordCredential, preventSilentAccess } from "@/lib/credentials";
import { mapCurrentDevice } from "@/lib/device";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        const token = localStorage.getItem("gg_token");
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            // Silently refresh the device mapping on any auth check
            mapCurrentDevice();
        } catch {
            localStorage.removeItem("gg_token");
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        if (typeof window !== "undefined" && window.location.hash?.includes("token=")) {
            setLoading(false);
            return;
        }
        fetchMe();
    }, [fetchMe]);

    // Reflect the user's generation on <body> so CSS can theme the aurora
    useEffect(() => {
        if (typeof document === "undefined") return;
        const gen = user?.generation || "";
        if (gen) {
            document.body.setAttribute("data-generation", gen);
        } else {
            document.body.removeAttribute("data-generation");
        }
    }, [user?.generation]);

    const setSession = useCallback((token, userData) => {
        localStorage.setItem("gg_token", token);
        setUser(userData);
        setLoading(false);
        // Map this device to the newly signed-in user
        mapCurrentDevice();
    }, []);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setSession(data.token, data.user);
            // Offer the credential to the browser's password manager
            storePasswordCredential({
                email,
                password,
                name: data.user?.name,
                avatar: data.user?.avatar,
            });
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    }, [setSession]);

    const signup = useCallback(async (payload) => {
        try {
            const { data } = await api.post("/auth/signup", payload);
            setSession(data.token, data.user);
            // Store after first successful signup too
            if (payload.email && payload.password) {
                storePasswordCredential({
                    email: payload.email,
                    password: payload.password,
                    name: data.user?.name,
                    avatar: data.user?.avatar,
                });
            }
            return { ok: true, welcome_voucher: data.welcome_voucher };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    }, [setSession]);

    const logout = useCallback(() => {
        localStorage.removeItem("gg_token");
        setUser(null);
        // Stop the browser auto-signing back in until the user explicitly does so again
        preventSilentAccess();
    }, []);

    // Instant vibe switch: optimistically recolor, then persist to the profile.
    const setGeneration = useCallback(async (gen) => {
        setUser((prev) => (prev ? { ...prev, generation: gen } : prev));
        try {
            await api.patch("/auth/me", { generation: gen });
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
        return { ok: true };
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, signup, logout, refresh: fetchMe, setSession, setGeneration }),
        [user, loading, login, signup, logout, fetchMe, setSession, setGeneration]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
